import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  DirectionsBoat as BoatIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  ConfirmationNumber as TicketIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Today as TodayIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  MonetizationOn,
  Percent,
  Shield,
  LocalOffer,
  AttachMoney
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getReservationById } from '../firebase/services/reservationService';
import { Reservation } from '../models/types';
import { format, isPast, isToday, parseISO } from 'date-fns';
import { formatTime12Hour, formatPhoneNumber } from '../utils/formatters';

const ReservationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReservation = async () => {
      if (!id) return;
      
      try {
        console.log('Fetching reservation with ID:', id);
        setLoading(true);
        const reservationData = await getReservationById(id);
        console.log('Received reservation data:', reservationData);
        
        if (!reservationData) {
          console.log('No reservation data found');
          setError('Reservation not found');
          return;
        }
        
        // Staff can view any reservation (no permission check like customer version)
        console.log('Setting reservation data:', reservationData);
        setReservation(reservationData);
      } catch (err) {
        console.error('Error fetching reservation:', err);
        setError('Failed to load reservation details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReservation();
  }, [id]);

  // Function to capitalize first letter of each word
  const capitalizeWords = (str: string) => {
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !reservation) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Reservation not found'}
        </Alert>
        <Button
          component={Link}
          to="/staff/dashboard"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const isPastReservation = isPast(new Date(reservation.date)) && !isToday(new Date(reservation.date));
  const isTodays = isToday(new Date(reservation.date));
  const isAdmin = userProfile?.role?.name === 'admin';

  const price = reservation.totalPrice ?? reservation.finalPrice ?? reservation.originalPrice;
  const dateObj = parseISO(reservation.date);

  // Payment history and balance due calculation
  const paymentHistory = Array.isArray(reservation.paymentHistory) ? reservation.paymentHistory : [];
  const totalPayments = paymentHistory
    .filter(p => !p.refunded)
    .reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalRefunds = paymentHistory
    .filter(p => p.refunded)
    .reduce((sum, p) => sum + (p.refundAmount || 0), 0);
  const balanceDue = price !== undefined && price !== null ? (Number(price) - totalPayments + totalRefunds) : null;

  // Payment status logic
  let paymentStatusLabel = 'Paid';
  let paymentStatusColor: 'success' | 'warning' | 'error' = 'success';
  if (balanceDue > 0.01) {
    paymentStatusLabel = totalPayments > 0 ? 'Partially Paid' : 'Pending';
    paymentStatusColor = totalPayments > 0 ? 'warning' : 'error';
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Staff - Reservation Details
          </Typography>
          <Box>
            {isAdmin && (
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                component={Link}
                to={`/staff/edit/${reservation.id}`}
              >
                Edit Reservation
              </Button>
            )}
            <Button
              component={Link}
              to="/staff/dashboard"
              startIcon={<ArrowBackIcon />}
              color="secondary"
              variant="outlined"
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BoatIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h5" component="div">
                    {reservation.vesselName || capitalizeWords(reservation.vesselType)}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary={<><PersonIcon fontSize="small" sx={{ mr: 1 }} /> <b>Customer</b></>} 
                      secondary={`${reservation.customerDetails.firstName} ${reservation.customerDetails.lastName}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={<><EmailIcon fontSize="small" sx={{ mr: 1 }} /> <b>Email</b></>} 
                      secondary={reservation.customerDetails.email} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={<><PhoneIcon fontSize="small" sx={{ mr: 1 }} /> <b>Phone</b></>} 
                      secondary={formatPhoneNumber(reservation.customerDetails.phone)} 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Reservation Information</Typography>
                  <Chip 
                    icon={isTodays ? <TodayIcon /> : isPastReservation ? <HistoryIcon /> : <TicketIcon />} 
                    label={isTodays ? 'Today' : isPastReservation ? 'Past' : capitalizeWords(reservation.status)}
                    color={
                      isTodays ? 'warning' : 
                      isPastReservation ? 'default' : 
                      reservation.status === 'confirmed' ? 'success' : 
                      reservation.status === 'pending' ? 'primary' : 
                      'error'
                    }
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary={<><CalendarIcon fontSize="small" sx={{ mr: 1 }} /> <b>Date & Time</b></>} 
                      secondary={`${format(dateObj, 'EEEE, MMMM d, yyyy')} • ${formatTime12Hour(reservation.startTime)} - ${formatTime12Hour(reservation.endTime)}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={<><GroupIcon fontSize="small" sx={{ mr: 1 }} /> <b>Party Size</b></>} 
                      secondary={`${reservation.partySize} ${reservation.partySize === 1 ? 'person' : 'people'}`} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary={<><TicketIcon fontSize="small" sx={{ mr: 1 }} /> <b>Reservation ID</b></>} 
                      secondary={reservation.id} 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Payment Information</Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Total Price</Typography>
                  <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {price !== undefined && price !== null ? `$${Number(price).toFixed(2)}` : 'N/A'}
                  </Typography>
                </Box>
                {/* Price Breakdown */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2, boxShadow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Price Breakdown</Typography>
                  <Box sx={{ pl: 1 }}>
                    {reservation.originalPrice !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <AttachMoney sx={{ fontSize: 18, color: 'primary.main', mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>Base Rate:</Typography>
                        <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700 }}>${Number(reservation.originalPrice).toFixed(2)}</Typography>
                      </Box>
                    )}
                    {reservation.taxAmount !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Percent sx={{ fontSize: 18, color: 'secondary.main', mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>Tax:</Typography>
                        <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700 }}>${Number(reservation.taxAmount).toFixed(2)}</Typography>
                      </Box>
                    )}
                    {reservation.damageWaiverAmount !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Shield sx={{ fontSize: 18, color: 'info.main', mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>Damage Waiver:</Typography>
                        <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700 }}>${Number(reservation.damageWaiverAmount).toFixed(2)}</Typography>
                      </Box>
                    )}
                    {reservation.discountAmount !== undefined && reservation.discountAmount > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <LocalOffer sx={{ fontSize: 18, color: 'error.main', mr: 1 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>Discount:</Typography>
                        <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700, color: 'red' }}>- ${Number(reservation.discountAmount).toFixed(2)}</Typography>
                      </Box>
                    )}
                    {/* Add more breakdown fields as needed */}
                    <Box sx={{ borderTop: '1px solid #eee', mt: 1, pt: 1, display: 'flex', alignItems: 'center' }}>
                      <MonetizationOn sx={{ fontSize: 18, color: 'success.main', mr: 1 }} />
                      <Typography variant="body2" sx={{ flex: 1, fontWeight: 'bold' }}>Total:</Typography>
                      <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 700 }}>{price !== undefined && price !== null ? `$${Number(price).toFixed(2)}` : 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={1} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                <Box sx={{ borderLeft: '2px solid #eee', height: '100%' }} />
              </Grid>
              <Grid item xs={12} md={5}>
                {paymentHistory.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Payments Made</Typography>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', background: 'none' }}>
                      <Box component="thead">
                        <Box component="tr" sx={{ bgcolor: 'grey.100' }}>
                          <Box component="th" sx={{ textAlign: 'left', pr: 2, fontWeight: 600, color: 'text.secondary', py: 1 }}>Type</Box>
                          <Box component="th" sx={{ textAlign: 'left', pr: 2, fontWeight: 600, color: 'text.secondary', py: 1 }}>Amount</Box>
                          <Box component="th" sx={{ textAlign: 'left', fontWeight: 600, color: 'text.secondary', py: 1 }}>Date</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {paymentHistory.map((p, idx) => (
                          <Box component="tr" key={p.id || idx} sx={{ borderBottom: '1px solid #eee' }}>
                            <Box component="td" sx={{ pr: 2, py: 1, fontWeight: 500 }}>
                              {p.refunded ? 'Refund' : p.depositPaid ? 'Deposit' : 'Payment'}
                            </Box>
                            <Box component="td" sx={{ pr: 2, py: 1, fontWeight: 700 }}>
                              ${Number(p.refunded ? p.refundAmount : p.amount).toFixed(2)}
                            </Box>
                            <Box component="td" sx={{ py: 1 }}>
                              {p.paymentDate ? format(parseISO(p.paymentDate), 'MMM d, yyyy') : ''}
                              {p.refunded && p.refundReason && (
                                <span style={{ color: '#888', marginLeft: 8 }}>({p.refundReason})</span>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                {/* Payment Status and Balance Due below Payments Made */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Payment Status</Typography>
                  <Chip label={paymentStatusLabel} color={paymentStatusColor} sx={{ mt: 1, fontWeight: 600, fontSize: 16, px: 2, py: 1 }} />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">Balance Due</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: balanceDue === 0 ? 'success.main' : 'error.main' }}>
                    {balanceDue !== null ? `$${balanceDue.toFixed(2)}` : 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default ReservationDetailsPage; 